import { useMutation, useQueryClient } from "@tanstack/react-query";

import { ENDPOINTS } from "@/api/application-api";
import { useUserToken } from "@/context/auth-context";
import { ToastType } from "@/enums";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { jsonAuthHeaders } from "@/utils/headers";
import { displayErrorToast, displayToast } from "@/utils/toast";

interface UpdateWorkspaceMemberRolesRequest {
  workspaceId: number;
  memberId: number;
  roleIds: number[];
}

export const useUpdateWorkspaceMemberRoles = () => {
  const userToken = useUserToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ workspaceId, memberId, roleIds }: UpdateWorkspaceMemberRolesRequest) => {
      const response = await fetch(ENDPOINTS.update_workspace_member_roles(workspaceId, memberId), {
        method: "PUT",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify({ roles: roleIds }),
      });
      return handleResponse(response);
    },
    onSuccess: () => {
      displayToast("Member roles updated successfully", ToastType.success);
      queryClient.invalidateQueries({
        queryKey: ["workspace-member-roles"],
      });
      queryClient.invalidateQueries({
        queryKey: ["workspace-members"],
      });
      queryClient.invalidateQueries({
        queryKey: [ENDPOINTS.organization_members],
      });
    },
    onError: (error: unknown) => {
      displayErrorToast(
        typeof error === "string" ? error : "Failed to update member roles. Please try again.",
      );
    },
  });
};
